import { useMemo, useState } from 'react';
import { CircleDashed, Plus, Trash2 } from 'lucide-react';

import { useUrlState } from '@/hooks/useUrlState';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { cyclesHooks } from '../queries';
import type { Cycle, Project } from '../types';
import { FilterSelect, FiltersPopover, ListSearch } from '@/components/ui/list-filters';

type CycleStatus = Cycle['status'];

const TABS: { value: CycleStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'completed', label: 'Completed' },
];

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

export function ProjectCyclesTab({ project }: { project: Project }) {
  const query = cyclesHooks.useList({ project: project.id });
  const remove = cyclesHooks.useRemove();
  // Guests read cycles but can't manage them (create/delete stay staff).
  const isGuest = useSession((s) => s.role) === 'guest';
  const [tab, setTab] = useUrlState<CycleStatus>('cycleTab', 'active');
  const [createOpen, setCreateOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [scheduleFil, setScheduleFil] = useState('all');

  const cycles = useMemo(() => rows<Cycle>(query.data), [query.data]);
  const inTab = cycles.filter((c) => c.status === tab);
  const q = search.trim().toLowerCase();
  const visible = inTab.filter((c) => {
    const scheduled = !!(c.start_date || c.end_date);
    return (
      (!q || c.name.toLowerCase().includes(q)) &&
      (scheduleFil === 'all' || (scheduleFil === 'scheduled' ? scheduled : !scheduled))
    );
  });
  const activeFilters = scheduleFil !== 'all' ? 1 : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b pb-3">
        <div className="-mb-3 flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium',
                tab === t.value
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {!isGuest ? (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Add cycle
          </Button>
        ) : null}
      </div>

      {cycles.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch value={search} onChange={setSearch} placeholder="Search cycles…" />
          <FiltersPopover activeFilters={activeFilters} onClear={() => setScheduleFil('all')}>
            <FilterSelect
              label="Schedule"
              value={scheduleFil}
              onChange={setScheduleFil}
              allLabel="All cycles"
              options={[
                { value: 'scheduled', label: 'Scheduled (has dates)' },
                { value: 'unscheduled', label: 'Unscheduled' },
              ]}
            />
          </FiltersPopover>
        </div>
      ) : null}

      {query.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : inTab.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <CircleDashed className="text-muted-foreground/30 size-16" strokeWidth={1.25} />
          <h2 className="mt-6 text-lg font-semibold">Group and timebox your work in Cycles.</h2>
          <p className="text-muted-foreground mt-1 max-w-md text-sm">
            Break work down by timeboxed chunks, work backwards from your project deadline to set dates,
            and make tangible progress as a team.
          </p>
          {!isGuest ? (
            <Button className="mt-5" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Set your first cycle
            </Button>
          ) : null}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-muted-foreground py-10 text-center text-sm">No cycles match your filters.</p>
      ) : (
        <ul className="space-y-2">
          {visible.map((c) => (
            <li key={c.id} className="flex items-center gap-3 rounded-md border px-3 py-2.5">
              <CircleDashed className="text-muted-foreground size-4 shrink-0" />
              <span className="flex-1 truncate text-sm font-medium">{c.name}</span>
              {c.start_date || c.end_date ? (
                <span className="text-muted-foreground text-xs">
                  {c.start_date ?? '…'} → {c.end_date ?? '…'}
                </span>
              ) : null}
              {!isGuest ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => remove.mutate(c.public_id)}
                  aria-label={`Delete ${c.name}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <CycleCreateDialog project={project} open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function CycleCreateDialog({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = cyclesHooks.useCreate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  function reset() {
    setName('');
    setDescription('');
    setStart('');
    setEnd('');
  }

  function submit() {
    if (!name.trim()) return;
    create.mutate(
      {
        project: project.id,
        name: name.trim(),
        description: description.trim() || undefined,
        start_date: start || null,
        end_date: end || null,
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="bg-muted/60 flex items-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-normal">
              <span>{project.icon || '📁'}</span> {project.name}
            </span>
            Create cycle
          </DialogTitle>
        </DialogHeader>

        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Title"
          className="h-auto border-0 px-0 text-base shadow-none focus-visible:ring-0"
        />
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
          rows={3}
        />

        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 w-40" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 w-40" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create cycle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
